import React, { memo, useState } from 'react'
import {
  Book,
  ChevronDown,
  ChevronRight,
  StickyNote,
  User,
  type LucideIcon,
} from 'lucide-react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'

const HighlightQuotes = ({ text }: { text: string }) => {
  // 同时识别中文引号 “” 与英文直引号 ""（修复 Bug-6：原正则只认中文引号）
  const parts = text.split(/(“[^”]*”|"[^"]*")/g)
  return (
    <>
      {parts.map((part, i) => {
        const isCn = part.length >= 2 && part.startsWith('“') && part.endsWith('”')
        const isEn = part.length >= 2 && part.startsWith('"') && part.endsWith('"')
        if (isCn || isEn) {
          const open = part[0]
          const close = part[part.length - 1]
          const content = part.slice(1, -1)
          return (
            <React.Fragment key={i}>
              {open}
              <span className='text-primary font-bold'>{content}</span>
              {close}
            </React.Fragment>
          )
        }
        return part
      })}
    </>
  )
}

const renderWithHighlights = (children: React.ReactNode) => {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return <HighlightQuotes text={child} />
    }
    return child
  })
}

// 通用可折叠块组件
const CollapsibleBlock = ({
  title,
  icon: Icon,
  content,
}: {
  title: string
  icon: LucideIcon
  content: string
}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className='my-3'>
      <CollapsibleTrigger className='bg-accent text-accent-foreground hover:bg-accent/80 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all'>
        <Icon className='h-4 w-4 shrink-0' />
        <span className='flex-1 text-left'>{title}</span>
        {isOpen ? (
          <ChevronDown className='h-4 w-4 shrink-0 transition-transform' />
        ) : (
          <ChevronRight className='h-4 w-4 shrink-0 transition-transform' />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className='overflow-hidden data-[state=closed]:animate-[collapsible-up_200ms_ease-out] data-[state=open]:animate-[collapsible-down_200ms_ease-out]'>
        <div className='border-border bg-muted text-muted-foreground mt-1 rounded-lg border px-3 py-2 text-sm'>
          <MarkdownContent>{content}</MarkdownContent>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// 解析带有特殊标签的内容
interface ContentPart {
  type: 'text' | 'note' | 'world_info' | 'char_info'
  content: string
}

const parseSpecialBlocks = (text: string): ContentPart[] => {
  const parts: ContentPart[] = []
  // 匹配 <tag>...</tag>，支持 note, world_info, char_info
  const regex = /<(note|world_info|char_info)>([\s\S]*?)<\/\1>/gi
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    // 添加标签之前的文本
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index).trim()
      if (textBefore) {
        parts.push({ type: 'text', content: textBefore })
      }
    }
    // 添加特殊块
    const tagType = match[1].toLowerCase() as
      | 'note'
      | 'world_info'
      | 'char_info'
    parts.push({ type: tagType, content: match[2].trim() })
    lastIndex = match.index + match[0].length
  }

  // 添加剩余的文本
  if (lastIndex < text.length) {
    const remaining = text.slice(lastIndex).trim()
    if (remaining) {
      parts.push({ type: 'text', content: remaining })
    }
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }]
}

// 纯 Markdown 渲染组件（不处理 note 标签）
const MarkdownContent = ({ children }: { children: string }) => {
  const components: Components = {
    p: ({ node: _node, children, ...props }) => {
      return (
        <p className='mb-2 last:mb-0' {...props}>
          {renderWithHighlights(children)}
        </p>
      )
    },
    code: ({ node: _node, className, children, ref: _ref, ...props }) => {
      // react-markdown v10 不再传入 inline 属性，原逻辑中 inline 恒为 undefined，等价于仅判断 match
      const match = /language-(\w+)/.exec(className || '')
      return match ? (
        <pre
          {...props}
          className={`${className} mt-2 max-w-full overflow-x-auto rounded-lg bg-zinc-100 p-3 text-sm wrap-break-word whitespace-pre-wrap dark:bg-zinc-800`}
        >
          <code className={`${match[1]} break-all`}>{children}</code>
        </pre>
      ) : (
        <code
          className={`${className} rounded-md bg-zinc-100 px-1 py-0.5 text-sm dark:bg-zinc-800`}
          {...props}
        >
          {children}
        </code>
      )
    },
    ol: ({ node: _node, children, ...props }) => {
      return (
        <ol className='ml-4 list-outside list-decimal' {...props}>
          {children}
        </ol>
      )
    },
    li: ({ node: _node, children, ...props }) => {
      return (
        <li className='py-1' {...props}>
          {renderWithHighlights(children)}
        </li>
      )
    },
    ul: ({ node: _node, children, ...props }) => {
      return (
        <ul className='ml-4 list-outside list-disc' {...props}>
          {children}
        </ul>
      )
    },
    strong: ({ node: _node, children, ...props }) => {
      return (
        <span className='font-semibold' {...props}>
          {renderWithHighlights(children)}
        </span>
      )
    },
    em: ({ node: _node, children, ...props }) => {
      return (
        <span className='italic' {...props}>
          {renderWithHighlights(children)}
        </span>
      )
    },
    a: ({ node: _node, children, ...props }) => {
      // Markdown 链接为内容/外部链接，使用原生 <a>（TanStack Link 用于内部路由且需要 to）
      return (
        <a
          className='text-blue-500 hover:underline'
          target='_blank'
          rel='noreferrer'
          {...props}
        >
          {renderWithHighlights(children)}
        </a>
      )
    },
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={components}
    >
      {children}
    </ReactMarkdown>
  )
}

// 主 Markdown 组件（处理特殊标签）
const NonMemoizedMarkdown = ({ children }: { children: string }) => {
  const parts = parseSpecialBlocks(children)

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part.type === 'world_info' && (
            <CollapsibleBlock
              title='世界信息'
              icon={Book}
              content={part.content}
            />
          )}
          {part.type === 'char_info' && (
            <CollapsibleBlock
              title='角色信息'
              icon={User}
              content={part.content}
            />
          )}
          {part.type === 'note' && (
            <CollapsibleBlock
              title='行动指南'
              icon={StickyNote}
              content={part.content}
            />
          )}
          {part.type === 'text' && (
            <MarkdownContent>{part.content}</MarkdownContent>
          )}
        </React.Fragment>
      ))}
    </>
  )
}

export const Markdown = memo(
  NonMemoizedMarkdown,
  (prevProps, nextProps) => prevProps.children === nextProps.children
)
